import { uniqueId } from 'lodash';
import React, { ReactElement, useCallback } from 'react';
import { css } from '@emotion/react';
import { CloseIcon } from '$/images/Icons';
import { theme } from 'twin.macro';
import tinycolor from 'tinycolor2';
import { useLocation } from '@reach/router';
import queryString from 'query-string';
import { cleanupError } from '../ui/MessageText';
import isMobile from '$/utils/isMobile';
import { useHeaderContext } from '$/sections/Home/HeaderContext';
import Button from '../ui/Button';

export enum ToastMessageType {
  Success = 'Success',
  Error = 'Error',
  Info = 'Info',
}

interface ToastMessageInput {
  message?: string;
  render?: (message: ToastMessage) => React.ReactElement;
  type: ToastMessageType;
  durationMs?: number;
  clickClose?: boolean;
  cb?: () => void;
}

export interface ToastMessage extends ToastMessageInput {
  id: string;
}

export interface ToastState {
  messages: ToastMessage[];
}

interface ToastContext extends ToastState {
  show: (message: ToastMessageInput) => string;
  hide: (id: string) => void;
}

const defaultToastContext = {
  messages: [],
  show: (): string => '',
  hide: (): void => {},
};

export const ToastContext = React.createContext<ToastContext>(defaultToastContext);
export const useToast = (): ToastContext => React.useContext(ToastContext);

export const WithToast: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const tmpMessages = React.useRef<ToastMessage[]>([]);
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);
  const updateMessage = (): void => setMessages(tmpMessages.current);
  const hide = useCallback((id: string): void => {
    tmpMessages.current = tmpMessages.current.filter((message) => message.id !== id);
    updateMessage();
  }, []);

  const show = useCallback(
    (message: ToastMessageInput): string => {
      const id = uniqueId();
      if (message.clickClose) {
        message.durationMs = 0;
      }
      const duration = message.durationMs ?? 6000;
      message.message = cleanupError(message.message);
      tmpMessages.current = [
        {
          id,
          message: message.message,
          render: message.render,
          type: message.type || ToastMessageType.Error,
          durationMs: duration,
          cb: message.cb,
        },
      ];
      updateMessage();
      duration &&
        setTimeout(() => {
          hide(id);
          message.cb && message.cb();
        }, duration);
      return id;
    },
    [hide]
  );

  const location = useLocation();
  React.useEffect(() => {
    // Show message in toast if sent by a server OAuth redirect

    const query = queryString.parse(location.search);
    if (query.tmsg) {
      show({ message: decodeURIComponent(query.tmsg as string), type: ToastMessageType.Success });
    }

    if (query.terror) {
      show({ message: decodeURIComponent(query.terror as string), type: ToastMessageType.Error });
    }
  }, [location, show]);

  return (
    <ToastContext.Provider
      value={{
        messages,
        show,
        hide,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

const getToastColor = (message?: ToastMessage): string => {
  if (!message) {
    return '#000000';
  }

  switch (message.type) {
    case ToastMessageType.Success:
      return theme`colors.blue.3`.toString();
    case ToastMessageType.Info:
      return theme`colors.blue.3`.toString();
    case ToastMessageType.Error:
      return theme`colors.coral.1`.toString();
  }
};

export const StickyToast = (): ReactElement => {
  const { messages, hide } = React.useContext(ToastContext);
  const { setHasBanner } = useHeaderContext();
  const message = messages[0];
  const bgColor = tinycolor(getToastColor(message));

  React.useEffect(
    () => setHasBanner && setHasBanner(messages.length > 0),
    [messages.length, setHasBanner]
  );

  return (
    <div
      className='relative w-full text-lg'
      css={css`
        z-index: -1;
        height: 0;
        visibility: ${message ? 'visible' : 'hidden'};
      `}
    >
      <div
        key={message?.id}
        className={`flex items-center content-center text-center`}
        css={css`
          position: fixed;
          top: ${isMobile ? '73px' : '100px'};
          left: 0;
          right: 0;
          color: #fff;
          background-color: ${bgColor.toHexString()};
          z-index: -1;
          height: ${isMobile ? '60px' : '40px'};
          opacity: ${message ? 1 : 0};
          transition:
            opacity 0.5s,
            transform 0.5s;
        `}
      >
        {message && (
          <div
            className='container mx-auto relative'
            css={css`
              max-width: calc(100% - 1.5rem) !important;
            `}
          >
            {message.render && message.render(message)}
            {message.message && message.message}
            {!message.durationMs && (
              <Button.icon
                className='absolute'
                css={css`
                  right: 5px;
                  top: calc(50% - 0.3rem);
                `}
                icon={<CloseIcon className='h-3/4e w-3/4e text-white' />}
                onClick={(): void => {
                  message.cb && message.cb();
                  hide(message.id);
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
